import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import GatedButton from "./GatedButton";

/**
 * Carte « Corps en composants » (Réglages → Intégrité du site) : pilote le job de maintenance
 * POST /api/admin/maintenance/migrate-bodies, qui convertit les corps HTML hérités (chaînes)
 * vers le format en composants, document par document, via le pipeline de sauvegarde canonique.
 *
 * Deux actions : « Analyser » (dryRun=true : compte ce qui serait converti sans rien écrire) et
 * « Réparer tout » (dryRun=false : convertit réellement, avec confirmation en ligne). Le backend
 * n'exécute qu'UNE tâche de maintenance à la fois (409 = créneau occupé) et publie la progression
 * via GET /api/admin/maintenance/migrate-bodies/status, sondé ici toutes les ~1,5 s. Le statut
 * est sérialisé en camelCase (Newtonsoft) avec l'état en chaîne : Idle|Running|Completed|Failed,
 * enveloppé dans { status: … } : exactement comme le statut du hard-clean juste au-dessus.
 *
 * Au montage, le statut est lu une fois : si un run est déjà en cours (onglet rouvert pendant une
 * réparation), la carte se rattache au sondage au lieu d'afficher l'état repos.
 */

export type MigrateBodiesState = "Idle" | "Running" | "Completed" | "Failed";

export interface MigrateBodiesStatus {
	state: MigrateBodiesState;
	dryRun: boolean;
	total: number;
	done: number;
	converted: number;
	skipped: number;
	failed: number;
	startedAtUtc?: string | null;
	finishedAtUtc?: string | null;
	error?: string | null;
	perType?: Record<string, number> | null;
}

const BUSY_MESSAGE = "Une autre tâche de maintenance est en cours.";

// Même convention d'appel que ContentItem.tsx (deployment-links) : axios + base REACT_APP_API_URL
// + withCredentials : le token Auth0 est attaché par l'intercepteur axios global.
const apiBase = () => (process.env.REACT_APP_API_URL ?? "").replace(/\/+$/, "");

interface Props {
	/** Intervalle de sondage du statut, en ms (réduit dans les tests). */
	pollIntervalMs?: number;
}

const BodyMigrationCard: React.FC<Props> = ({ pollIntervalMs = 1500 }) => {
	const [busy, setBusy] = useState(false);
	const [mode, setMode] = useState<"analyse" | "repair" | null>(null);
	const [status, setStatus] = useState<MigrateBodiesStatus | null>(null);
	const [message, setMessage] = useState<string | null>(null);
	const [confirming, setConfirming] = useState(false);
	const cancelledRef = useRef(false);

	useEffect(() => {
		cancelledRef.current = false;
		return () => {
			cancelledRef.current = true;
		};
	}, []);

	const fetchStatus = async (): Promise<MigrateBodiesStatus | null> => {
		try {
			const res = await axios.get(`${apiBase()}/api/admin/maintenance/migrate-bodies/status`, {
				withCredentials: true
			});
			return (res?.data?.status as MigrateBodiesStatus) ?? null;
		} catch {
			return null;
		}
	};

	const finish = (s: MigrateBodiesStatus) => {
		setStatus(s);
		setBusy(false);
		if (s.state === "Failed") {
			setMessage(`Échec : ${s.error ?? "erreur inconnue"}.`);
			return;
		}
		if (s.dryRun) {
			setMessage(`${s.converted} corps à convertir sur ${s.total} document(s).`);
		} else {
			setMessage(
				`Terminé : ${s.converted} corps converti(s), ${s.skipped} ignoré(s), ${s.failed} en échec, sur ${s.total} document(s).`
			);
		}
	};

	// Seule l'OBSERVATION est bornée (30 min), pas le job : au-delà on cesse de sonder et on
	// indique que le traitement continue côté serveur : même contrat que les pollers des Réglages.
	const poll = async () => {
		const started = Date.now();
		const maxMs = 30 * 60 * 1000;
		while (!cancelledRef.current) {
			const s = await fetchStatus();
			if (cancelledRef.current) return;
			if (s) {
				setStatus(s);
				if (s.state === "Completed" || s.state === "Failed") {
					finish(s);
					return;
				}
			}
			if (Date.now() - started > maxMs) {
				setBusy(false);
				setMessage("Toujours en cours. Le traitement continue côté serveur : revenez consulter le statut plus tard.");
				return;
			}
			await new Promise((r) => setTimeout(r, pollIntervalMs));
		}
	};

	// Au montage : si un run est déjà en cours, se rattacher au sondage au lieu d'afficher le repos.
	useEffect(() => {
		let alive = true;
		(async () => {
			const s = await fetchStatus();
			if (!alive || cancelledRef.current || !s || s.state !== "Running") return;
			setStatus(s);
			setMode(s.dryRun ? "analyse" : "repair");
			setBusy(true);
			setMessage(s.dryRun ? "Analyse en cours…" : "Réparation en cours…");
			void poll();
		})();
		return () => {
			alive = false;
		};
	}, []);

	const start = async (dryRun: boolean) => {
		if (busy) return;
		setConfirming(false);
		setMode(dryRun ? "analyse" : "repair");
		setBusy(true);
		setStatus(null);
		setMessage(dryRun ? "Analyse en cours…" : "Réparation en cours…");
		try {
			await axios.post(`${apiBase()}/api/admin/maintenance/migrate-bodies?dryRun=${dryRun}`, null, {
				withCredentials: true
			});
			await poll();
		} catch (err: any) {
			setBusy(false);
			if (err?.response?.status === 409) {
				setMessage(BUSY_MESSAGE);
				return;
			}
			setMessage(String(err?.response?.data?.error ?? err?.message ?? "Échec du démarrage de la tâche."));
		}
	};

	const total = status?.total ?? 0;
	const done = status?.done ?? 0;
	const pct = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0;
	const perTypeEntries = Object.entries(status?.perType ?? {}).sort((a, b) => b[1] - a[1]);

	return (
		<div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm space-y-3">
			<div>
				<p className="text-base font-semibold text-slate-800">Corps en composants</p>
				<p className="text-sm text-slate-600 max-w-2xl">
					Les corps des contenus hérités sont stockés sous forme de chaînes HTML. Cette réparation les
					convertit un par un vers le nouveau format en composants, via le pipeline de sauvegarde
					canonique : le rendu publié reste strictement identique. L'opération est idempotente : les
					corps déjà convertis sont ignorés. Lancez d'abord une analyse pour mesurer l'ampleur, puis la
					réparation.
				</p>
			</div>

			{/* Les deux actions frappent le POST de maintenance (dryRun compris) → settings:write. */}
			<div className="flex flex-wrap items-center gap-3">
				<GatedButton
					token="feature:settings:write"
					onClick={() => start(true)}
					disabled={busy}
					className="inline-flex items-center gap-2 rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-60"
				>
					{busy && mode === "analyse" ? "Analyse en cours…" : "Analyser"}
				</GatedButton>
				{!confirming ? (
					<GatedButton
						token="feature:settings:write"
						onClick={() => setConfirming(true)}
						disabled={busy}
						className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
					>
						{busy && mode === "repair" ? "Réparation en cours…" : "Réparer tout"}
					</GatedButton>
				) : (
					<span className="inline-flex flex-wrap items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5">
						<span className="text-sm text-slate-700">Convertir définitivement tous les corps concernés ?</span>
						<button
							type="button"
							onClick={() => start(false)}
							className="inline-flex items-center rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700"
						>
							Confirmer la réparation
						</button>
						<button
							type="button"
							onClick={() => setConfirming(false)}
							className="inline-flex items-center rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50"
						>
							Annuler
						</button>
					</span>
				)}
			</div>

			{busy && mode === "repair" && (
				<div className="space-y-2">
					<div
						className="h-2 w-full overflow-hidden rounded-full bg-slate-100"
						role="progressbar"
						aria-valuemin={0}
						aria-valuemax={total}
						aria-valuenow={done}
					>
						<div className="h-full rounded-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} />
					</div>
					<p className="text-xs text-slate-600">
						{done}/{total} document(s) : {status?.converted ?? 0} converti(s) · {status?.skipped ?? 0} ignoré(s) ·{" "}
						{status?.failed ?? 0} échec(s)
					</p>
				</div>
			)}

			{busy && mode === "analyse" && total > 0 && (
				<p className="text-xs text-slate-600">
					{done}/{total} document(s) analysé(s) : {status?.converted ?? 0} à convertir
				</p>
			)}

			{message && <p className="text-sm text-slate-600">{message}</p>}

			{perTypeEntries.length > 0 && (
				<div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
					<table className="w-full text-xs text-slate-600">
						<thead>
							<tr className="text-left">
								<th className="py-1 font-semibold">Type de contenu</th>
								<th className="py-1 text-right font-semibold">Corps convertis</th>
							</tr>
						</thead>
						<tbody>
							{perTypeEntries.map(([key, count]) => (
								<tr key={key}>
									<td className="py-0.5">{key}</td>
									<td className="py-0.5 text-right">{count}</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}
		</div>
	);
};

export default BodyMigrationCard;
